import { useEffect, useRef } from 'react';
import { pushBackHandler } from '../utils/backStack';

// 모달/팝업 공통 — ESC 키 + 뒤로가기(안드로이드 하드웨어 back/브라우저 back)로 닫기.
// enabled=false 면 비활성(중첩 팝업 제어용).
// onClose 는 ref 로 최신본 참조 — 인라인 콜백이어도 히스토리 등록이 렌더마다 재실행되지 않게 [enabled] 에만 의존.
//
// 🪜 ESC 는 **맨 위(가장 나중에 열린) 창 하나만** 닫는다 (2026-11-04 형 "부적합 사진을 팝업으로 키워 보다 ESC 누르면
//   입력 화면 전체가 닫힌다 — 단계별로"). 종전엔 창마다 document keydown 을 따로 달아 stopPropagation 이 같은 노드의
//   다른 리스너를 막지 못해, 위·아래 창이 한 번에 닫혔다. 이제 전역 리스너 1개 + 열린 순서 스택.
//   다른 곳(입력칸·팝오버)이 ESC 를 이미 처리해 preventDefault 했으면 창은 닫지 않는다.
type Entry = { close: { current: () => void } };
const stack: Entry[] = [];
let bound = false;
function onKey(e: KeyboardEvent) {
	if (e.key !== 'Escape' || e.defaultPrevented) return;
	const top = stack[stack.length - 1];
	if (!top) return;
	e.preventDefault();
	e.stopPropagation();
	top.close.current();
}

export function useEscapeClose(onClose: () => void, enabled = true) {
	const closeRef = useRef(onClose);
	closeRef.current = onClose;
	useEffect(() => {
		if (!enabled) return;
		const entry: Entry = { close: closeRef };
		stack.push(entry);
		// window 버블 = document 리스너·React 입력칸 처리 **뒤** — 팝오버·입력칸이 먼저 ESC 를 쓰고 preventDefault 하면 창은 그대로
		if (!bound) { window.addEventListener('keydown', onKey); bound = true; }
		const unregBack = pushBackHandler(() => closeRef.current());
		return () => {
			const i = stack.lastIndexOf(entry);
			if (i >= 0) stack.splice(i, 1);
			unregBack();
		};
	}, [enabled]);
}
